// src/components/Canvas3D.tsx

import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import * as THREE from 'three';
import type { BuildingForRender, RoadForRender } from '../hooks/useCityData';
import type { LocalPoint } from '../utils/osmParser';
import { registerScene } from '../utils/gltf';
import { buildRoadRibbon } from '../utils/roadGeometry';
import CityScene from './CityScene';
import ViewportControls from './ViewportControls';

// ----------------------------------------------------------------------
// Tipos de props
// ----------------------------------------------------------------------

export interface Canvas3DProps {
  /** Latitud central */
  lat: number;
  /** Longitud central */
  lng: number;
  /** Radio de búsqueda en metros */
  radius?: number;
  /** Si se deben cargar calles */
  includeRoads?: boolean;
  /** URL de un JSON local (public/data/*.json) para cargar sin llamar a la API */
  seedUrl?: string;
  /** Nombre del sitio (para nombrar archivos exportados) */
  siteName?: string;
}

// ----------------------------------------------------------------------
// Registra la escena actual para poder exportarla (GLTF)
// ----------------------------------------------------------------------

const SceneRegistry: React.FC = () => {
  const scene = useThree((state) => state.scene);
  useEffect(() => {
    registerScene(scene);
  }, [scene]);
  return null;
};

// ----------------------------------------------------------------------
// Utilidad de exportación de anillos a coordenadas [x, z] cerradas
// ----------------------------------------------------------------------

type RingPoint = LocalPoint;

/** Convierte un anillo en una lista de puntos [x, z] cerrando el contorno. */
const ringToClosedPoints = (ring: RingPoint[]): number[][] => {
  const points = ring.map((p) => [p.x, p.z]);
  if (ring.length > 0) points.push([ring[0].x, ring[0].z]);
  return points;
};

/** Convierte los anillos interiores (huecos) a listas de puntos cerrados. */
const holesToClosedPoints = (innerRings: LocalPoint[][]): number[][][] =>
  (innerRings || [])
    .filter((ring) => ring.length >= 3)
    .map((ring) => ringToClosedPoints(ring));

// Eje de un eje cartesiano (línea + punta + etiqueta)
const Axis: React.FC<{
  length: number;
  color: string;
  label: string;
  to: [number, number, number];
  labelPos: [number, number, number];
}> = ({ length, color, label, to, labelPos }) => {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(...to),
    ]);
    return g;
  }, [to]);

  const cone = useMemo(() => new THREE.ConeGeometry(5, 16, 16), []);

  // Orientar el cono según la dirección del eje
  let conePos: [number, number, number] = [0, 0, 0];
  let coneRot: [number, number, number] = [0, 0, 0];
  if (to[0] > 0) {
    conePos = [length, 0, 0];
    coneRot = [0, 0, -Math.PI / 2];
  } else if (to[1] > 0) {
    conePos = [0, length, 0];
    coneRot = [0, 0, 0];
  } else if (to[2] > 0) {
    conePos = [0, 0, length];
    coneRot = [Math.PI / 2, 0, 0];
  }

  const material = useMemo(() => new THREE.MeshBasicMaterial({ color }), [color]);
  const lineMaterial = useMemo(
    () => new THREE.LineBasicMaterial({ color }),
    [color]
  );

  const text = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 44px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, 64, 32);
    const tex = new THREE.CanvasTexture(canvas);
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: tex, color, depthTest: false })
    );
    sprite.scale.set(40, 20, 1);
    sprite.position.set(...labelPos);
    return sprite;
  }, [label, color, labelPos]);

  const groupRef = React.useRef<THREE.Group>(null);
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.add(text);
    }
    return () => {
      geometry.dispose();
      cone.dispose();
      material.dispose();
      lineMaterial.dispose();
      if (text.material.map) text.material.map.dispose();
      text.material.dispose();
    };
  }, [geometry, cone, material, lineMaterial, text]);

  return (
    <group ref={groupRef}>
      <line>
        <primitive object={geometry} attach="geometry" />
        <primitive object={lineMaterial} attach="material" />
      </line>
      <mesh geometry={cone} material={material} position={conePos} rotation={coneRot} />
    </group>
  );
};

// Eje cartesiano completo en el origen (0,0,0)
const CartesianAxes: React.FC<{ size: number }> = ({ size }) => {
  return (
    <group>
      <Axis length={size} color="#ff3b30" label="X" to={[size, 0, 0]} labelPos={[size + 20, 0, 0]} />
      <Axis length={size} color="#34c759" label="Y" to={[0, size, 0]} labelPos={[0, size + 20, 0]} />
      <Axis length={size} color="#007aff" label="Z" to={[0, 0, size]} labelPos={[0, 0, size + 20]} />
    </group>
  );
};

// ----------------------------------------------------------------------
// Componente Canvas3D principal
// ----------------------------------------------------------------------

const Canvas3D: React.FC<Canvas3DProps> = ({
  lat,
  lng,
  radius = 500,
  includeRoads = true,
  seedUrl,
  siteName,
}) => {
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingForRender | null>(null);
  const [selectedRoad, setSelectedRoad] = useState<RoadForRender | null>(null);
  const [showBuildings, setShowBuildings] = useState(true);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [roadsData, setRoadsData] = useState<RoadForRender[]>([]);
  const [buildingsData, setBuildingsData] = useState<BuildingForRender[]>([]);
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<number | string>>(new Set());

  // Limpiar selecciones y objetos ocultos al cambiar de ubicación
  useEffect(() => {
    setSelectedBuilding(null);
    setSelectedRoad(null);
    setHiddenIds(new Set());
  }, [lat, lng]);

  const handleSelectBuilding = useCallback(
    (building: BuildingForRender | null) => {
      setSelectedBuilding(building);
      if (building) setSelectedRoad(null);
    },
    []
  );

  const handleSelectRoad = useCallback((road: RoadForRender | null) => {
    setSelectedRoad(road);
    if (road) setSelectedBuilding(null);
  }, []);

  // Borrar el objeto seleccionado de la representación 3D (no de los datos)
  const handleDeleteSelected = useCallback(() => {
    const id = selectedBuilding?.id ?? selectedRoad?.id;
    if (id === undefined || id === null) return;
    setHiddenIds((prev) => new Set(prev).add(id));
    setSelectedBuilding(null);
    setSelectedRoad(null);
  }, [selectedBuilding, selectedRoad]);

  const handleRoadsReady = useCallback((roads: RoadForRender[]) => {
    setRoadsData(roads);
  }, []);

  const handleBuildingsReady = useCallback((buildings: BuildingForRender[]) => {
    setBuildingsData(buildings);
  }, []);

  // Exportar toda la escena (edificios y calles como polígonos cerrados)
  const handleExportScene = useCallback(() => {
    if (roadsData.length === 0 && buildingsData.length === 0) return;
    const slug = (siteName || `${lat}_${lng}`)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const buildings = buildingsData
      .filter((b) => !hiddenIds?.has(b.id))
      .map((b) => ({
        type: 'building',
        id: b.id,
        name: b.name ?? '',
        points: ringToClosedPoints(b.outerRing),
        holes: holesToClosedPoints(b.innerRings),
      }));

    const roads = roadsData
      .filter((r) => !hiddenIds?.has(r.id))
      .map((r) => ({
        type: 'road',
        id: r.id,
        name: r.name ?? '',
        lanes: r.lanes,
        oneway: r.oneway,
        points: buildRoadRibbon(r.points, r.width || 8).map((p) => [p.x, p.z]),
      }));

    const payload = {
      exportedAt: new Date().toISOString(),
      site: { name: siteName, lat, lng, radius },
      type: 'scene',
      objects: [...buildings, ...roads],
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `escena_${slug}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [roadsData, buildingsData, hiddenIds, lat, lng, radius, siteName]);

  // Exportar el objeto seleccionado (edificio o calle) a JSON (formato simple)
  const handleExportSelected = useCallback(() => {
    let payload: Record<string, unknown> | null = null;
    let filename = 'seleccion';

    if (selectedBuilding) {
      payload = {
        type: 'building',
        id: selectedBuilding.id,
        name: selectedBuilding.name ?? '',
        points: ringToClosedPoints(selectedBuilding.outerRing),
        holes: holesToClosedPoints(selectedBuilding.innerRings),
      };
      filename =
        (selectedBuilding.name || `edificio_${selectedBuilding.id}`)
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'edificio';
    } else if (selectedRoad) {
      const ribbon = buildRoadRibbon(selectedRoad.points, selectedRoad.width || 8);
      payload = {
        type: 'road',
        id: selectedRoad.id,
        name: selectedRoad.name ?? '',
        lanes: selectedRoad.lanes,
        oneway: selectedRoad.oneway,
        points: ribbon.map((p) => [p.x, p.z]),
      };
      filename =
        (selectedRoad.name || `calle_${selectedRoad.id}`)
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'calle';
    }

    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [selectedBuilding, selectedRoad]);

  // Resolución del shadow map: se mantiene 2048 en sitios pequeños y escala
  // en los grandes para que la sombra no se vea bloqueada.
  const shadowMapSize = Math.max(2048, Math.round(radius * 4));

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Canvas
        shadows
        camera={{
          position: [radius, radius, radius],
          fov: 60,
          near: 0.1,
          far: radius * 20,
        }}
        style={{ background: '#87CEEB' }}
      >
        {/* Luces */}
        <ambientLight intensity={0.5} />
        <directionalLight
          position={[100, 200, 50]}
          intensity={1.5}
          castShadow
          shadow-mapSize-width={shadowMapSize}
          shadow-mapSize-height={shadowMapSize}
          shadow-camera-left={-radius}
          shadow-camera-right={radius}
          shadow-camera-top={radius}
          shadow-camera-bottom={-radius}
          shadow-camera-near={10}
          shadow-camera-far={radius * 2.5}
        />

        <Suspense fallback={null}>
          <SceneRegistry />
          <CityScene
            lat={lat}
            lng={lng}
            radius={radius}
            includeRoads={includeRoads}
            onSelectBuilding={handleSelectBuilding}
            selectedBuildingId={selectedBuilding?.id ?? null}
            onSelectRoad={handleSelectRoad}
            selectedRoadId={selectedRoad?.id ?? null}
            seedUrl={seedUrl}
            showBuildings={showBuildings}
            reloadSignal={refreshSignal}
            onRoadsReady={handleRoadsReady}
            onBuildingsReady={handleBuildingsReady}
            hiddenIds={hiddenIds}
          />
        </Suspense>

        {/* Controles de navegación */}
        <OrbitControls
          makeDefault
          target={[0, 0, 0]}
          maxPolarAngle={Math.PI / 2.2}
          minDistance={50}
          maxDistance={radius * 5}
        />

        {/* Cuadrícula de referencia */}
        <Grid
          position={[0, 0, 0]}
          args={[radius * 8, radius * 8]}
          cellSize={10}
          cellThickness={0.5}
          cellColor="#444444"
          sectionSize={50}
          sectionThickness={1}
          sectionColor="#666666"
          fadeDistance={radius * 4}
          fadeStrength={1}
        />

        {/* Eje cartesiano con origen en (0,0,0) */}
        <CartesianAxes size={radius * 0.5} />
      </Canvas>

      {/* Overlay y controles del viewport */}
      <ViewportControls
        selectedBuilding={selectedBuilding}
        selectedRoad={selectedRoad}
        showBuildings={showBuildings}
        onToggleBuildings={() => setShowBuildings((v) => !v)}
        onRefresh={() => setRefreshSignal((s) => s + 1)}
        onExportScene={handleExportScene}
        onExportSelected={handleExportSelected}
        onDeleteSelected={handleDeleteSelected}
        hasSelection={Boolean(selectedBuilding || selectedRoad)}
        objectsCount={roadsData.length + buildingsData.length}
      />
    </div>
  );
};

export default Canvas3D;